import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
  ElementRef,
  ViewChild,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  AbstractControl,
  UntypedFormControl,
  FormGroup,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  map,
  distinctUntilChanged,
  debounceTime,
  switchMap,
  tap,
  filter,
  first,
  takeUntil,
} from 'rxjs/operators';
import {
  ContactDetailsLookupService,
  FormTemplateBuilder,
} from './contact-details-lookup.service';
import { isEqual } from 'lodash-es';
import {
  slideInOut,
  fadeInAnimation,
  BaseComponent,
  ContactDetailsFormValuesContainer,
  Claim,
  ContactDetailsFormValues,
  AddressItem,
  PcaService,
  ClaimsAnalyticsService,
  AddressItemDetail,
} from '@domgen/dgx-components';

@Component({
  selector: 'claims-contact-information',
  templateUrl: './contact-information.component.html',
  styleUrls: ['./contact-information.component.scss'],
  providers: [ContactDetailsLookupService],
  animations: [slideInOut, fadeInAnimation],
})
export class ContactInformationComponent
  extends BaseComponent
  implements OnInit, OnChanges
{
  @Input() contactDetails?: ContactDetailsFormValuesContainer;
  @Input() claim?: Claim;
  @Input() placeholder?: string;
  @Input() searchLabel = 'Address';
  @Input() heading = 'Your details';
  @Input() defaultEdit = false;
  @Input() showCancelCTA = true;
  @Output() submitted = new EventEmitter();
  @Output() save: EventEmitter<ContactDetailsFormValues> = new EventEmitter();
  @Output() valid: EventEmitter<boolean> = new EventEmitter();
  @Output() editSave: EventEmitter<boolean> = new EventEmitter();
  @ViewChild('error') errorEl?: ElementRef | null;
  @ViewChild('scrollList') scrollList?: ElementRef | null;
  @ViewChild('contactAddressInput') contactAddressInput?: ElementRef | null;

  addressForm?: UntypedFormGroup;
  isEdit = false;
  isLandLordOwner = false;
  focused = false;
  selected = 0;
  initFormDataValid = false;
  isManualAddress = true;
  addressList: AddressItem[] = [];
  formTemplateBuilder?: FormTemplateBuilder[];
  f?: {
    [key: string]: AbstractControl;
  }; // shortcut for form controls reference

  private addressFormSub?: Subscription;

  constructor(
    private fb: UntypedFormBuilder,
    private pca: PcaService,
    private analytics: ClaimsAnalyticsService,
    private lookup: ContactDetailsLookupService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit() {
    this.formTemplateBuilder = this.lookup.getFormBuilderList(
      this.contactDetails as ContactDetailsFormValuesContainer
    );

    this.isLandLordOwner = this.claim.reflect.isRiskOwner === 'false';

    this.createForm();

    if (this.defaultEdit) {
      this.edit({ emit: false });
    }

    if (this.addressForm?.valid) {
      this.save.emit(this.addressForm.value);
      this.initFormDataValid = true;
    }

    this.scrollToPosition();
  }

  onKeydown(event: Event, index: number, addressList: AddressItem[]) {
    if ((event as KeyboardEvent).key === 'Enter') {
      event.preventDefault();
      this.handleAddressClick(addressList[index]);
    } else if (
      (event as KeyboardEvent).key === 'ArrowDown' &&
      this.selected < addressList.length - 1
    ) {
      this.selected++;
      (this.scrollList as ElementRef).nativeElement.scrollTop =
        this.selected * 51 - 51;
    } else if (
      (event as KeyboardEvent).key === 'ArrowUp' &&
      this.selected > 0
    ) {
      this.selected--;
      (this.scrollList as ElementRef).nativeElement.scrollTop =
        this.selected * 51;
    }
  }

  public formatCase(name: string, ctrl: UntypedFormControl) {
    if (name === 'postcode') {
      ctrl.patchValue(ctrl.value.toUpperCase());
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      !isEqual(
        changes?.contactDetails?.currentValue,
        changes?.contactDetails?.previousValue
      )
    ) {
      this.createForm();
    }
  }

  createForm() {
    this.addressForm = this.fb.group(
      this.lookup.getLookupFormGroup(
        this.contactDetails as ContactDetailsFormValuesContainer
      )
    );

    this.addressForm.addValidators((form: FormGroup) => {
      const { mobile, landline } = form.value;

      if (mobile.trim() === '' && landline.trim() === '') {
        return {
          mobileOrLandlineRequired: true,
        };
      }

      return null;
    });

    this.addressForm.updateValueAndValidity();

    if (this.addressForm.invalid) {
      this.edit();
      this.isManualAddress = true;
      this.addressForm.markAllAsTouched();
    }

    this.f = this.addressForm.controls;
  }

  edit(config = { emit: true }) {
    if (!this.isEdit) {
      this.onEditGA();
    }

    this.isEdit = true;
    this.isManualAddress = false;
    if (config.emit) this.valid.emit(false);
    this.initCapturePlusField();
    this.focus();
    this.scrollToPosition();
  }

  private focus() {
    setTimeout(() => {
      const target = this.contactAddressInput?.nativeElement;
      if (target) {
        target.focus();
        target.click();
      }
    }); // push end of stack
  }

  private initCapturePlusField() {
    // subscribe to address field changes for Capture+
    if (this.addressFormSub) {
      this.addressFormSub.unsubscribe();
    }
    this.addressFormSub = this.addressForm
      ?.get('address')
      ?.valueChanges.pipe(
        takeUntil(this.destroy$),
        map((input: string) => input.trim()),
        distinctUntilChanged(),
        debounceTime(200),
        tap((input: string) => {
          if (input.length === 0) {
            this.addressList = [];
            this.cdr.detectChanges();
          }
        }),
        filter((input) => !!input),
        switchMap((input: string) =>
          this.pca.pcaFind(input).pipe(
            map(({ Items = [] }: { Items: AddressItem[] }) => {
              this.addressList = this.updateList(Items);
              this.cdr.detectChanges();
            })
          )
        )
      )
      .subscribe();
  }

  onBlur(e) {
    const blurOutside = !e?.relatedTarget?.classList?.contains(
      'address-look-up__item'
    );
    if (blurOutside) this.focused = false;
  }

  handleAddressClick(addressLookup: AddressItem) {
    if (addressLookup?.Type !== null && addressLookup?.Type === 'Address') {
      this.addressList = [];

      this.pca
        .pcaRetrieve(addressLookup.Id)
        .pipe(
          takeUntil(this.destroy$),
          first(),
          map((res) => {
            return res.Items[0];
          })
        )
        .subscribe((res: AddressItemDetail) => {
          this.addressForm
            ?.get('address')
            ?.setValue(
              `${res.Line1} ${res.Line2} ${res.City} ${res.PostalCode}`,
              { emitEvent: false }
            );

          this.addressForm?.patchValue({
            addressLine1: res.Line1,
            addressLine2: res.Line2,
            city: res.City,
            county: res.County ? res.County : '',
            postcode: res.PostalCode,
          });
        });
    } else if (addressLookup?.Text) {
      this.pca
        .pcaFind(addressLookup.Text, addressLookup.Id)
        .pipe(takeUntil(this.destroy$))
        .subscribe(({ Items }) => {
          this.addressList = this.updateList(Items);
          this.selected = 0;
          (this.scrollList as ElementRef).nativeElement.scrollTop = 0;
          this.cdr.detectChanges();
        });
    }
  }

  get addressValue() {
    return this.addressForm?.get('address').value;
  }

  clearInput() {
    this.addressForm?.get('address').setValue('');
  }

  updateList(items: AddressItem[]) {
    const userInputValue = this.addressForm?.get('address').value;

    const list = [];
    const regex = new RegExp(userInputValue, 'gi');

    if (items.length === 1 && items[0].Error === '1001') {
      return [
        {
          ...items[0],
          Description: 'No match found',
        },
      ];
    }

    if (items.length === 0) {
      return [
        {
          Error: true,
          Description: 'No match found',
        },
      ];
    }

    for (let i = 0; i < items.length; i++) {
      const match = `${items[i].Text}, ${items[i].Description}`.match(regex);

      let result;

      if (match) {
        const formatted = `${items[i].Text}, ${items[i].Description}`.replace(
          regex,
          `<span class="highlight">${match[0]}</span>`
        );

        result = formatted;
      } else {
        result = items[i].Error
          ? 'No match found'
          : `${items[i].Text}, ${items[i].Description}`;
      }

      list.push({
        ...items[i],
        Description: result,
      });
    }
    return list;
  }

  handleExpandable(address: AddressItem): boolean {
    return !!(address.Type === 'Address' || address.Next === 'Retrieve');
  }

  toggleAddress() {
    this.focus();
    this.isManualAddress = !this.isManualAddress;
    if (!this.isManualAddress) {
      this.initCapturePlusField();
      this.scrollToPosition();
    }
  }

  updateForm() {
    if (this.addressForm?.valid) {
      this.contactDetails = {
        formValues: this.addressForm?.value as ContactDetailsFormValues,
      };
      this.addressForm?.get('address')?.setValue('');
      this.addressList = [];
      this.isEdit = false;
      this.initFormDataValid = true;
      this.valid.emit(true);
      this.save.emit(this.addressForm.value);
      this.editSave.emit(true);
    } else {
      this.addressForm?.markAllAsTouched();
      this.valid.emit(false);
      this.getFormValidationMessages().forEach((m) => {
        this.triggerGAError(m);
      });

      setTimeout(() => {
        // dom ready
        if (this.errorEl?.nativeElement) {
          this.errorEl.nativeElement.scrollIntoView({ behavior: 'smooth' });
        }
      });
    }
  }

  cancel() {
    if (this.initFormDataValid) {
      this.isEdit = false;
      this.isManualAddress = false;
      this.createForm();
      this.valid.emit(true);
    }

    this.scrollToPosition();
  }

  triggerGAError(validationMessage: string) {
    this.analytics.claimEventExtended({
      eventClaims: 'validation-error',
      eventAction: 'validation-error-dgx',
      eventLabel: validationMessage,
      claim: this.claim as Claim,
    });
  }

  onEditGA() {
    this.analytics.claimEventExtended({
      eventClaims: 'contact-details-edit',
      eventAction: 'edit-personal-details',
      eventLabel: 'edit-personal-details',
      claim: this.claim as Claim,
    });
  }

  private getFormValidationMessages() {
    const validationMessages: string[] = [];
    for (const key in this.addressForm?.controls) {
      if (!this.addressForm?.controls[key].valid) {
        const formBuilderElement = this.lookup.getFormBuilderElement(
          key,
          this.contactDetails as ContactDetailsFormValuesContainer
        );
        if (this.addressForm?.controls[key].errors?.required) {
          validationMessages.push(formBuilderElement.requiredMessage);
        }
        if (this.addressForm?.controls[key].errors?.pattern) {
          validationMessages.push(formBuilderElement.patternMessage);
        }
      }
    }
    return validationMessages;
  }

  scrollToPosition() {
    setTimeout(() => {
      // user experience delay
      const region = document.getElementById('ContactSection');
      if (region) {
        region.scrollIntoView({ behavior: 'smooth' });
      }
    }, 200);
  }
}
